import { useEffect, useRef } from "react";
import { useAuth } from "./useAuth";

type UserRealtimeHandlers = {
	onNotificationCreated?: (payload: {
		userId: string;
		id: string;
		type: string;
		title: string;
		body: string;
		data?: string;
		createdAt?: number;
	}) => void;
	onChatMessage?: (payload: {
		id: string;
		conversationId: string;
		senderId: string;
		content: string;
		replyToId?: string;
		createdAt: number;
		editedAt?: number;
		deletedAt?: number;
		senderName: string;
		attachments: { id: string; r2Key: string; fileName: string; mimeType: string; fileSize: number; createdAt: number }[];
	}) => void;
	onChatRead?: (payload: {
		conversationId: string;
		userId: string;
		lastReadAt: number;
	}) => void;
	onChatTyping?: (payload: {
		userId: string;
		isTyping: boolean;
	}) => void;
	onCallIncoming?: (payload: {
		callerId: string;
		callerName: string;
		room: string;
	}) => void;
	onCallEnd?: (payload: {
		callerId: string;
	}) => void;
};

export function useUserRealtime(handlers: UserRealtimeHandlers): void {
	const { getToken, isSignedIn } = useAuth();

	const getTokenRef = useRef(getToken);
	useEffect(() => {
		getTokenRef.current = getToken;
	}, [getToken]);

	const handlersRef = useRef(handlers);
	useEffect(() => {
		handlersRef.current = handlers;
	});

	useEffect(() => {
		if (!isSignedIn) return;

		let cancelled = false;
		let socket: WebSocket | null = null;
		let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
		let pingTimer: ReturnType<typeof setInterval> | null = null;
		let retryCount = 0;

		const scheduleReconnect = () => {
			if (cancelled) return;
			if (retryCount >= 7) return;
			const delay = Math.min(30_000, 1000 * 2 ** retryCount);
			retryCount += 1;
			reconnectTimer = setTimeout(connect, delay);
		};

		const connect = async () => {
			if (cancelled) return;
			try {
				const token = await getTokenRef.current();
				if (!token || cancelled) {
					scheduleReconnect();
					return;
				}
				const proto = window.location.protocol === "https:" ? "wss" : "ws";
				const url = `${proto}://${window.location.host}/api/realtime/user?token=${encodeURIComponent(token)}`;
				socket = new WebSocket(url);

				socket.addEventListener("open", () => {
					retryCount = 0;
					pingTimer = setInterval(() => {
						if (socket?.readyState === WebSocket.OPEN) {
							try {
								socket.send("ping");
							} catch {
								// ignore
							}
						}
					}, 30_000);
				});

				socket.addEventListener("message", (e) => {
					if (typeof e.data !== "string") return;
					try {
						const event = JSON.parse(e.data) as { type: string; payload: unknown };
						const h = handlersRef.current;
						if (event.type === "notification.created") {
						h.onNotificationCreated?.(event.payload as {
							userId: string;
							id: string;
							type: string;
							title: string;
							body: string;
							createdAt?: number;
						});
						} else if (event.type === "chat.message") {
						h.onChatMessage?.(event.payload as {
							id: string;
							conversationId: string;
							senderId: string;
							content: string;
							replyToId?: string;
							createdAt: number;
							editedAt?: number;
							deletedAt?: number;
							senderName: string;
							attachments: { id: string; r2Key: string; fileName: string; mimeType: string; fileSize: number; createdAt: number }[];
						});
						} else if (event.type === "chat.read") {
						h.onChatRead?.(event.payload as {
							conversationId: string;
							userId: string;
							lastReadAt: number;
						});
						} else if (event.type === "chat.typing") {
						h.onChatTyping?.(event.payload as {
							userId: string;
							isTyping: boolean;
						});
						} else if (event.type === "call.incoming") {
						h.onCallIncoming?.(event.payload as {
							callerId: string;
							callerName: string;
							room: string;
						});
						} else if (event.type === "call.end") {
						h.onCallEnd?.(event.payload as {
							callerId: string;
						});
						}
					} catch {
						// ignore non-JSON frames
					}
				});

				socket.addEventListener("close", () => {
					if (pingTimer) clearInterval(pingTimer);
					pingTimer = null;
					scheduleReconnect();
				});

				socket.addEventListener("error", () => {
					try {
						socket?.close();
					} catch {
						// ignore
					}
				});
			} catch {
				scheduleReconnect();
			}
		};

		void connect();

		return () => {
			cancelled = true;
			if (reconnectTimer) clearTimeout(reconnectTimer);
			if (pingTimer) clearInterval(pingTimer);
			if (socket) {
				try {
					socket.close();
				} catch {
					// ignore
				}
			}
		};
	}, [isSignedIn]);
}
